import { ComponentProps } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/core/form';
import { Switch } from '@/components/core/switch';
import { cn } from '@/shared/helpers';

import { FormFieldProps } from './default-form-field';

type SwitchProps = Omit<ComponentProps<typeof Switch>, 'checked' | 'onCheckedChange' | 'name'>;

export function SwitchFormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  disabled,
  label,
  description,
  className,
  ...switchProps
}: FormFieldProps<SwitchProps, TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between">
          <div className="space-y-1">
            {label && <FormLabel>{label}</FormLabel>}
            {description && <FormDescription>{description}</FormDescription>}
          </div>
          <FormControl>
            <Switch
              {...switchProps}
              // The switch's default `after:-inset-x-3` widens its tap target into the
              // surrounding row gutter. In this full-width row that extra width is
              // redundant, and on the right it overflows narrow containers (e.g. side-panel
              // dialogs) into a stray horizontal scrollbar. Drop the horizontal overhang
              // (keep the vertical one).
              className={cn('after:inset-x-0', className)}
              checked={field.value ?? false}
              onCheckedChange={field.onChange}
              disabled={field.disabled}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}
